import { useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useDataFetcher from '../../hooks/useFetchData';
import { Knisot } from '../../../../../../modules/API/Knisot/knisot';
import { IEntrie } from '../../../../../../modules/API/Knisot/models/entrie';
import { RootState } from '../../../../../../../store/reducers/rootReducer';
import {
    CLEAR_ENTRY_DETAILS,
    LOAD,
    LOAD_END,
} from '../../../../../../../store/actions/actionsTypes';
import { ContextModal } from '../../../../../../modules/modal/modalContext';
import useWorkerInfo from '../../../../../../generic/hooks/useWorkerInfo';
import useCheckRights from '../../../../../../generic/hooks/useCheckRights';
import { filterEntries } from '../../helpers/filterEntries';
import { canCreateKnisa } from '../../helpers/knisotPermissions';
import { KnisotPrintHandler } from '../../types';

interface UseKnisotListParams {
    date: string;
    dateEnd: string;
    locationPath: string;
}

const useKnisotList = ({ date, dateEnd, locationPath }: UseKnisotListParams) => {
    const [t] = useTranslation('knisa');
    const dispatch = useDispatch();
    const [, setModal] = useContext(ContextModal);
    const { user, token } = useSelector((state: RootState) => state.mainReducer);

    const [input, setInput] = useState<string>('');

    const workerID = user && user.rights.includes('WORKER') ? +user.code_internal : 0;
    const { Role_108 } = useWorkerInfo({ workerID });
    const { hasRight: hasRightToCreate } = useCheckRights({ key: 'ת_כניסה' });

    const { isLoading, data } = useDataFetcher<IEntrie[]>(
        Knisot.getEntries,
        `DateFrom=${date}&DateTo=${dateEnd}`,
        false,
        locationPath,
    );

    const renderData = useMemo(() => filterEntries(data ?? [], input), [data, input]);

    const isAllowToCreate = useMemo(
        () => canCreateKnisa({ user, role108: Role_108, hasRightToCreate }),
        [user, Role_108, hasRightToCreate],
    );

    const prepareCreateEntry = useCallback(() => {
        dispatch({ type: CLEAR_ENTRY_DETAILS });
    }, [dispatch]);

    const handlePrintKnisa: KnisotPrintHandler = useCallback(
        async (entryNum) => {
            dispatch({ type: LOAD });
            try {
                const url = `entry/${entryNum}/pdf`;
                const response = await fetch(`https://my.agroline.co.il/v2/${url}`, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                });
                const blob = await response.blob();
                const file = new Blob([blob], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            } catch {
                setModal({
                    open: true,
                    value: t('knisa_notification_printEntryFailure', { num: entryNum }),
                    mood: false,
                });
                setTimeout(() => {
                    setModal({
                        open: false,
                        value: '',
                        mood: false,
                    });
                }, 3000);
            } finally {
                dispatch({ type: LOAD_END });
            }
        },
        [dispatch, setModal, token, t],
    );

    return {
        renderData,
        isLoading,
        input,
        setInput,
        isAllowToCreate,
        prepareCreateEntry,
        handlePrintKnisa,
    };
};

export default useKnisotList;
